import { useEffect, useMemo } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { UserProps } from '@img-arena/consoles-api-dde'
import { BreadcrumbSetter } from '@/components/Breadcrumbs'
import { CardTab, DataCardWithTabs } from '@/components/card'
import { TitleSetter } from '@/components/Title'
import { ROUTE_PARAM } from '@/router/constants'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { ddeApi } from '@/service/ddeApi'
import {
  useOperatorLicensesStoreDDE,
  useOperatorStoreDDE,
  useOperatorUsersStoreDDE
} from '@/stores/operator'

import { LicenseRow } from './Licenses/licenses.dde.table'
import {
  createOperatorTabs,
  getHeaderPostfixActions,
  getOperatorHeaderItems,
  OPERATOR_TABS
} from './operatorView.dde.table'

export const OperatorViewDDE = () => {
  const { operatorId, '*': tabParam } = useParams()
  const numericOperatorId = operatorId ? Number(operatorId) : undefined
  const navigate = useNavigate()

  const {
    data,
    fetchData: fetchOperator,
    resetState: resetOperator
  } = useOperatorStoreDDE(
    useShallow((state) => ({
      data: state.data,
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  const {
    data: licenses,
    fetchData: fetchOperatorLicenses,
    resetState: resetOperatorLicenses
  } = useOperatorLicensesStoreDDE(
    useShallow((state) => ({
      data: state.data,
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  const {
    data: users,
    fetchData: fetchOperatorUsers,
    resetState: resetOperatorUsers
  } = useOperatorUsersStoreDDE(
    useShallow((state) => ({
      data: state.data,
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    if (!numericOperatorId) return

    fetchOperator({ operatorId: numericOperatorId })
    fetchOperatorLicenses({ operatorId: numericOperatorId })
    fetchOperatorUsers({ operatorId: numericOperatorId })
    return () => {
      resetOperator()
      resetOperatorLicenses()
      resetOperatorUsers()
    }
  }, [numericOperatorId])

  const TABS = useMemo(
    () => createOperatorTabs(licenses.length, users.length),
    [licenses.length, users.length]
  )
  const DEFAULT_TAB = OPERATOR_TABS.LICENSES
  const tab = TABS.some((t) => t.value === tabParam) ? tabParam : undefined

  const operator = useMemo(
    () => (Array.isArray(data) && data.length > 0 && operatorId ? data[0] : null),
    [data, operatorId]
  )

  const title = operator ? `Operator - ${operator.name}` : 'Operator'

  const handleDeleteSelectedUsers = createBulkHandler({
    name: 'Delete Users',
    operation: async (user: UserProps) => {
      await ddeApi.endpoints.users.delete(user.userId)
    },
    refreshData: () => fetchOperatorUsers({ operatorId: numericOperatorId! }),
    getItemDisplayName: (user) => `${user.userName} (${user.userId})`
  })

  const handleSuspendSelectedLicenses = createBulkHandler({
    name: 'Suspend Licenses',
    operation: async (license: LicenseRow) => {
      await ddeApi.endpoints.operators.suspendOperatorLicense(numericOperatorId!, license.property)
    },
    refreshData: () => fetchOperatorLicenses({ operatorId: numericOperatorId! }),
    getItemDisplayName: (license) => license.property
  })

  const headerPostfixActions = useMemo(() => {
    if (!operatorId) return null

    return getHeaderPostfixActions({
      tab: tab || DEFAULT_TAB,
      operatorId,
      handleDeleteSelectedUsers,
      handleSuspendSelectedLicenses,
      fetchOperatorUsers,
      fetchOperatorLicenses
    })
  }, [
    tab,
    operatorId,
    handleDeleteSelectedUsers,
    handleSuspendSelectedLicenses,
    fetchOperatorUsers,
    fetchOperatorLicenses
  ])

  return (
    <>
      <TitleSetter title={title} />
      <BreadcrumbSetter param={ROUTE_PARAM.OPERATOR_ID} label={operator?.name} />
      <DataCardWithTabs
        title={title}
        details={getOperatorHeaderItems(operator)}
        tabs={TABS}
        currentTab={tab || DEFAULT_TAB}
        onTabChange={(tab) => navigate(`/operators/${operatorId}/${tab}`, { replace: true })}
        headerPostfixActions={headerPostfixActions}
      >
        <CardTab value={OPERATOR_TABS.LICENSES}>
          <Outlet />
        </CardTab>
        <CardTab value={OPERATOR_TABS.USERS}>
          <Outlet />
        </CardTab>
      </DataCardWithTabs>
    </>
  )
}
